import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Locale, LOCALES, Translations } from '../types/i18n.types';

@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly http = inject(HttpClient);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly locale = signal<Locale>(this.readLocale());
  readonly translations = signal<Translations | null>(null);

  constructor() {
    /* SSR: translations are provided synchronously via `setInitial` (see
       app.config.server.ts) — no HTTP kicked off, so the render doesn't
       wait on a hanging pending task. Browser: fetch as usual. */
    if (this.isBrowser) this.load(this.locale());
  }

  setLocale(locale: Locale): void {
    if (locale === this.locale()) return;
    this.locale.set(locale);
    if (this.isBrowser) localStorage.setItem('ds-locale', locale);
    this.load(locale);
  }

  /** SSR-only entry point — call from a server-side app initializer. */
  setInitial(t: Translations): void {
    this.translations.set(t);
  }

  private load(locale: Locale): void {
    this.http.get<Translations>(`/i18n/${locale}.json`).subscribe((t) => this.translations.set(t));
  }

  private readLocale(): Locale {
    if (!this.isBrowser) return 'en';

    const stored = localStorage.getItem('ds-locale');
    if (stored && (LOCALES as readonly string[]).includes(stored)) return stored as Locale;

    const nav = navigator.language.toLowerCase();
    if (nav.startsWith('fr')) return 'fr';
    if (nav.startsWith('de')) return 'de';
    if (nav.startsWith('it')) return 'it';

    return 'en';
  }
}
